import { createContext, useContext } from 'react'

import { LANGUAGES } from './translations'

/**
 * Contesto, hook e helper della lingua della pagina preventivo.
 *
 * Sta in un file separato dal provider perché contiene solo funzioni: il provider è un
 * componente, e tenerli insieme farebbe perdere il fast refresh in sviluppo.
 * La catena di risoluzione della lingua è documentata in `i18n.jsx`.
 */

export const QuoteI18nContext = createContext(null)

/** Normalizza un codice lingua a 'it'/'en', oppure undefined se non lo gestiamo. */
export const normalizeLanguage = (value) => {
  const code = String(value ?? '')
    .slice(0, 2)
    .toLowerCase()
  return LANGUAGES.includes(code) ? code : undefined
}

/**
 * Testi e lingua correnti della pagina preventivo.
 *
 * @returns {{language: string, locale: string, t: Object, changeLanguage: Function,
 *   adoptQuoteLanguage: Function}}
 */
export function useQuoteI18n() {
  const context = useContext(QuoteI18nContext)
  if (!context) throw new Error('useQuoteI18n richiede QuoteI18nProvider')
  return context
}

/**
 * Etichetta di un campo del form di booking.
 *
 * L'Executor manda `labelKey` (codice stabile) e `label` (italiano). I campi che Goelett
 * nomina da sé — campi analitici, approvatori — non hanno codice e nessun dizionario li
 * copre: si mostra il nome del provider così com'è.
 *
 * @param {Object} t - Dizionario corrente
 * @param {{labelKey?: string, label: string}} field - Campo del descrittore
 * @returns {string} Etichetta da mostrare
 */
export const fieldLabel = (t, field) =>
  (field.labelKey && t.form.fields[field.labelKey]) || field.label

/**
 * Titolo della sezione del form.
 *
 * @param {Object} t - Dizionario corrente
 * @param {{groupKey?: string, group: string}} field - Un campo della sezione
 * @returns {string} Titolo da mostrare
 */
export const groupLabel = (t, field) =>
  (field.groupKey && t.form.groups[field.groupKey]) || field.group

/**
 * Testo di aiuto del campo, tradotto quando lo conosciamo.
 *
 * @param {Object} t - Dizionario corrente
 * @param {{labelKey?: string, hint?: string}} field - Campo del descrittore
 * @returns {string|undefined} Hint da mostrare
 */
export const fieldHint = (t, field) =>
  (field.labelKey && t.form.hints[field.labelKey]) || field.hint
